import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { ChevronRight, Pencil, Plus, X, PauseCircle, PlayCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const SLOTS = ['AM', 'Midday', 'PM'];
const TURNOUT_SETTINGS = ['Individual', 'Group'];

const uid = () => Math.random().toString(36).slice(2);

const EMPTY_FEED = (slot) => ({ id: uid(), slot, care_catalog_item_id: null, type: '', name: '', serving: '', quantity: '', time: '', effective_from: '', is_paused: false, paused_since: null, pause_history: [] });
const EMPTY_MED  = (slot) => ({ id: uid(), slot, care_catalog_item_id: null, type: '', name: '', dose: '', units: '', time: '', effective_from: '', is_paused: false, paused_since: null, pause_history: [] });

function deepClone(obj) { return JSON.parse(JSON.stringify(obj)); }

// ── FeedRow ───────────────────────────────────────────────────────────────────

function FeedRow({ row, editing, feedCatalog, onChange, onRemove, onAdd, showSlot, showAdd, showTime }) {
  const catalogItem = feedCatalog.find(c => c.id === row.care_catalog_item_id);
  const isBillable  = !!catalogItem?.billable;
  const displayName = catalogItem
    ? `${catalogItem.name}${catalogItem.serving_size != null ? ` (${catalogItem.serving_size}${catalogItem.serving_size_units ? ' ' + catalogItem.serving_size_units : ''})` : ''}`
    : row.name || '';

  const handleNameChange = (itemId) => {
    if (itemId === '__none__') { onChange({ ...row, name: '', care_catalog_item_id: null, serving: '' }); return; }
    const item = feedCatalog.find(c => c.id === itemId);
    if (item) {
      const serving = item.serving_size != null
        ? `${item.serving_size}${item.serving_size_units ? ' ' + item.serving_size_units : ''}` : '';
      onChange({ ...row, care_catalog_item_id: item.id, type: item.type, name: item.name, serving });
    }
  };

  const slotCell = (
    <td className="py-1.5 px-2 text-xs w-14 bg-muted/30 text-center">
      {showSlot && <div className="font-semibold">{row.slot}</div>}
      {editing && row.care_catalog_item_id && (
        <div className="mt-1">
          <div className="text-[8px] text-muted-foreground mb-0.5">Eff. From</div>
          <input type="date" value={row.effective_from || ''}
            onChange={e => onChange({ ...row, effective_from: e.target.value })}
            className={`w-14 text-[8px] border rounded px-0.5 h-4 focus:outline-none ${row.effective_from ? 'time-filled' : ''}`} />
        </div>
      )}
      {showAdd && editing && (
        <button type="button" onClick={onAdd}
          className="flex items-center gap-0.5 text-[9px] text-primary hover:underline font-medium mx-auto mt-0.5">
          <Plus className="w-2.5 h-2.5" /> Add
        </button>
      )}
    </td>
  );

  if (!editing) {
    return (
      <tr className={`border-b last:border-0 ${row.is_paused ? 'opacity-40' : ''}`}>
        {slotCell}
        <td className="py-1.5 px-3 text-xs">
          <div className="flex items-center gap-1.5">
            {row.is_paused && <PauseCircle className="w-3 h-3 text-amber-500 shrink-0" />}
            <span>{displayName || <span className="text-muted-foreground">—</span>}</span>
          </div>
        </td>
        <td className="py-1.5 px-3 text-xs">{row.quantity || <span className="text-muted-foreground">—</span>}</td>
        {showTime && <td className="py-1.5 px-3 text-xs">{row.time || '—'}</td>}
      </tr>
    );
  }

  return (
    <tr className="border-b last:border-0">
      {slotCell}
      <td className="py-1.5 px-2">
        <Select value={row.care_catalog_item_id || '__none__'} onValueChange={handleNameChange}>
          <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Name…" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">— None —</SelectItem>
            {feedCatalog.map(item => (
              <SelectItem key={item.id} value={item.id}>
                {item.name}{item.serving_size != null ? ` (${item.serving_size}${item.serving_size_units ? ' ' + item.serving_size_units : ''})` : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="py-1.5 px-2 w-28">
        <Input className="h-7 text-xs" placeholder="e.g. 2 scoops"
          value={row.quantity} onChange={e => onChange({ ...row, quantity: e.target.value })} />
      </td>
      {showTime && (
        <td className="py-1.5 px-2 w-24">
          <input type="time"
            className={`h-7 text-xs border rounded px-2 w-24 focus:outline-none focus:ring-1 focus:ring-primary ${row.time ? 'time-filled' : ''}`}
            value={row.time || ''} onChange={e => onChange({ ...row, time: e.target.value })} />
        </td>
      )}
      <td className="py-1.5 px-1 w-14">
        <div className="flex items-center gap-0.5">
          <button onClick={() => {
              const today = new Date().toLocaleDateString('sv');
              if (!row.is_paused) {
                const hist = [...(row.pause_history || []), { paused_at: today, resumed_at: null }];
                onChange({ ...row, is_paused: true, paused_since: today, pause_history: hist });
              } else {
                const hist = (row.pause_history || []).map((p, i, a) =>
                  i === a.length - 1 && !p.resumed_at ? { ...p, resumed_at: today } : p
                );
                onChange({ ...row, is_paused: false, paused_since: null, pause_history: hist });
              }
            }}
            className={`transition-colors ${row.is_paused ? 'text-amber-500 hover:text-amber-600' : 'text-muted-foreground hover:text-amber-500'}`}
            title={row.is_paused ? 'Resume' : 'Pause'}>
            {row.is_paused ? <PlayCircle className="w-3.5 h-3.5" /> : <PauseCircle className="w-3.5 h-3.5" />}
          </button>
          <button onClick={onRemove} className="text-muted-foreground hover:text-destructive transition-colors" title="Remove">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </td>
    </tr>
  );
}

// ── MedRow ────────────────────────────────────────────────────────────────────

function MedRow({ row, editing, medCatalog, onChange, onRemove, onAdd, showSlot, showAdd, showTime }) {
  const typeOptions = [...new Set(medCatalog.map(c => c.type))].filter(Boolean);
  const nameOptions = row.type ? medCatalog.filter(c => c.type === row.type) : medCatalog;
  const displayName = row.name ? `${row.name}${row.dose ? ` (${row.dose})` : ''}` : '';
  const isBillable  = !!medCatalog.find(c => c.id === row.care_catalog_item_id)?.billable;

  const handleTypeChange = (val) => {
    onChange({ ...row, type: val === '__none__' ? '' : val, name: '', dose: '', care_catalog_item_id: null });
  };
  const handleNameChange = (itemId) => {
    if (itemId === '__none__') { onChange({ ...row, name: '', dose: '', care_catalog_item_id: null }); return; }
    const item = medCatalog.find(c => c.id === itemId);
    if (item) onChange({
      ...row, care_catalog_item_id: item.id, type: item.type, name: item.name,
      dose: item.dosage ? `${item.dosage} ${item.dosage_unit || ''}`.trim() : '',
    });
  };

  const slotCell = (
    <td className="py-1.5 px-2 text-xs w-14 bg-muted/30 text-center">
      {showSlot && <div className="font-semibold">{row.slot}</div>}
      {editing && row.care_catalog_item_id && (
        <div className="mt-1">
          <div className="text-[8px] text-muted-foreground mb-0.5">Eff. From</div>
          <input type="date" value={row.effective_from || ''}
            onChange={e => onChange({ ...row, effective_from: e.target.value })}
            className={`w-14 text-[8px] border rounded px-0.5 h-4 focus:outline-none ${row.effective_from ? 'time-filled' : ''}`} />
        </div>
      )}
      {showAdd && editing && (
        <button type="button" onClick={onAdd}
          className="flex items-center gap-0.5 text-[9px] text-primary hover:underline font-medium mx-auto mt-0.5">
          <Plus className="w-2.5 h-2.5" /> Add
        </button>
      )}
    </td>
  );

  if (!editing) {
    return (
      <tr className={`border-b last:border-0 ${row.is_paused ? 'opacity-40' : ''}`}>
        {slotCell}
        <td className="py-1.5 px-3 text-xs">{row.type || <span className="text-muted-foreground">—</span>}</td>
        <td className="py-1.5 px-3 text-xs">
          <div className="flex items-center gap-1.5">
            {row.is_paused && <PauseCircle className="w-3 h-3 text-amber-500 shrink-0" />}
            <span>{displayName || <span className="text-muted-foreground">—</span>}</span>
          </div>
        </td>
        <td className="py-1.5 px-3 text-xs">{row.units || <span className="text-muted-foreground">—</span>}</td>
        {showTime && <td className="py-1.5 px-3 text-xs">{row.time || '—'}</td>}
      </tr>
    );
  }

  return (
    <tr className="border-b last:border-0">
      {slotCell}
      <td className="py-1.5 px-2">
        <Select value={row.type || '__none__'} onValueChange={handleTypeChange}>
          <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Type…" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">— None —</SelectItem>
            {typeOptions.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </td>
      <td className="py-1.5 px-2">
        <Select value={row.care_catalog_item_id || '__none__'} onValueChange={handleNameChange}>
          <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Name…" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">— None —</SelectItem>
            {nameOptions.map(item => (
              <SelectItem key={item.id} value={item.id}>
                {item.name}{item.dosage ? ` (${item.dosage} ${item.dosage_unit || ''})` : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="py-1.5 px-2 w-24">
        <Input className="h-7 text-xs" type="number" min="0" step="0.1" placeholder="1"
          value={row.units} onChange={e => onChange({ ...row, units: e.target.value })} />
      </td>
      {showTime && (
        <td className="py-1.5 px-2 w-24">
          <input type="time"
            className={`h-7 text-xs border rounded px-2 w-24 focus:outline-none focus:ring-1 focus:ring-primary ${row.time ? 'time-filled' : ''}`}
            value={row.time || ''} onChange={e => onChange({ ...row, time: e.target.value })} />
        </td>
      )}
      <td className="py-1.5 px-1 w-14">
        <div className="flex items-center gap-0.5">
          <button onClick={() => {
              const today = new Date().toLocaleDateString('sv');
              if (!row.is_paused) {
                const hist = [...(row.pause_history || []), { paused_at: today, resumed_at: null }];
                onChange({ ...row, is_paused: true, paused_since: today, pause_history: hist });
              } else {
                const hist = (row.pause_history || []).map((p, i, a) =>
                  i === a.length - 1 && !p.resumed_at ? { ...p, resumed_at: today } : p
                );
                onChange({ ...row, is_paused: false, paused_since: null, pause_history: hist });
              }
            }}
            className={`transition-colors ${row.is_paused ? 'text-amber-500 hover:text-amber-600' : 'text-muted-foreground hover:text-amber-500'}`}
            title={row.is_paused ? 'Resume' : 'Pause'}>
            {row.is_paused ? <PlayCircle className="w-3.5 h-3.5" /> : <PauseCircle className="w-3.5 h-3.5" />}
          </button>
          <button onClick={onRemove} className="text-muted-foreground hover:text-destructive transition-colors" title="Remove">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </td>
    </tr>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function EverydayRoutineSection({ horse, locations, catalogItems, onSaved }) {
  const [dialogOpen, setDialogOpen]   = useState(false);
  const [editing, setEditing]         = useState(false);
  const [loading, setLoading]         = useState(false);
  const [saving, setSaving]           = useState(false);
  const [routine, setRoutine]         = useState(null);
  const [draft, setDraft]             = useState(null);

  const [feedSchedule,    setFeedSchedule]    = useState(false);
  const [medsSchedule,    setMedsSchedule]    = useState(false);
  const [turnoutSchedule, setTurnoutSchedule] = useState(false);

  const feedCatalog = (catalogItems || []).filter(c => c.category === 'Daily Care' && c.type === 'Feed');
  const medCatalog  = (catalogItems || []).filter(c => c.category === 'Medical' && ['Medication', 'Supplement'].includes(c.type));
  const boardingCatalog = (catalogItems || []).filter(c => c.category === 'Daily Care' && c.type === 'Boarding');

  // Ensure each entry has an id (backward-compat with old single-entry data)
  const ensureIds = (arr) => (arr || []).map(e => e.id ? e : { ...e, id: uid() });

  // Guarantee at least one row per slot so the table always has AM/Midday/PM visible
  const ensureSlots = (arr, emptyFn) => {
    const withIds = ensureIds(arr);
    const missing = SLOTS.filter(s => !withIds.some(e => e.slot === s));
    return [...withIds, ...missing.map(emptyFn)];
  };

  const fetchRoutine = useCallback(async () => {
    if (!horse?.id) return;
    setLoading(true);
    try {
      const res = await api.get(`/farmfolio/horse/${horse.id}/routine`);
      const data = res.data || {};
      const normalised = {
        ...data,
        feed_entries: ensureSlots(data.feed_entries, EMPTY_FEED),
        med_entries:  ensureSlots(data.med_entries,  EMPTY_MED),
        turnout:  data.turnout  || { location_id: '', location_name: '', setting_type: '', notes: '' },
        bedding:  data.bedding  || { type: '' },
        boarding: data.boarding || { care_catalog_item_id: '', name: '', effective_from: '', is_paused: false },
      };
      setRoutine(normalised);
    } catch { /* silently ignore */ }
    setLoading(false);
  }, [horse?.id]);

  useEffect(() => {
    if (dialogOpen && !routine && !loading) fetchRoutine();
  }, [dialogOpen, routine, loading, fetchRoutine]);

  const enterEdit = () => {
    const base = routine || {
      feed_entries: SLOTS.map(EMPTY_FEED),
      med_entries:  SLOTS.map(EMPTY_MED),
      turnout: { location_id: '', location_name: '', setting_type: '', notes: '' },
      bedding: { type: '' },
      boarding: { care_catalog_item_id: '', name: '', effective_from: '', is_paused: false },
    };
    const d = deepClone(base);
    d.feed_entries = ensureSlots(d.feed_entries, EMPTY_FEED);
    d.med_entries  = ensureSlots(d.med_entries,  EMPTY_MED);
    setDraft(d);
    setFeedSchedule(routine?.feed_schedule_enabled    || false);
    setMedsSchedule(routine?.meds_schedule_enabled    || false);
    setTurnoutSchedule(routine?.turnout_schedule_enabled || false);
    setEditing(true);
  };

  const cancelEdit = () => { setEditing(false); setDraft(null); };

  // ── Feed helpers ───────────────────────────────────────────────────────────
  const addFeedRow = (slot) => setDraft(d => ({ ...d, feed_entries: [...d.feed_entries, EMPTY_FEED(slot)] }));
  const removeFeedRow = (id) => setDraft(d => {
    const row = d.feed_entries.find(r => r.id === id);
    const catItem = row?.care_catalog_item_id ? feedCatalog.find(c => c.id === row.care_catalog_item_id) : null;
    const history = catItem?.billable && row.care_catalog_item_id ? [
      ...(d.routine_history || []),
      { care_catalog_item_id: row.care_catalog_item_id, category: catItem.category || 'Daily Care', type: catItem.type || 'Feed', name: catItem.name || row.name || '', cost: catItem.cost || 0, cost_currency: catItem.cost_currency || 'USD', effective_from: row.effective_from || null, effective_to: new Date().toLocaleDateString('sv'), slot: row.slot || '' },
    ] : (d.routine_history || []);
    const remaining = d.feed_entries.filter(r => r.id !== id);
    const slot = row?.slot;
    if (slot && !remaining.some(r => r.slot === slot)) remaining.push(EMPTY_FEED(slot));
    return { ...d, feed_entries: remaining, routine_history: history };
  });
  const updateFeedRow = (id, val) => setDraft(d => ({
    ...d, feed_entries: d.feed_entries.map(r => r.id === id ? val : r),
  }));

  // ── Med helpers ────────────────────────────────────────────────────────────
  const addMedRow = (slot) => setDraft(d => ({ ...d, med_entries: [...d.med_entries, EMPTY_MED(slot)] }));
  const removeMedRow = (id) => setDraft(d => {
    const row = d.med_entries.find(r => r.id === id);
    const catItem = row?.care_catalog_item_id ? medCatalog.find(c => c.id === row.care_catalog_item_id) : null;
    const history = catItem?.billable && row.care_catalog_item_id ? [
      ...(d.routine_history || []),
      { care_catalog_item_id: row.care_catalog_item_id, category: catItem.category || 'Medical', type: catItem.type || 'Medication', name: catItem.name || row.name || '', cost: catItem.cost || 0, cost_currency: catItem.cost_currency || 'USD', effective_from: row.effective_from || null, effective_to: new Date().toLocaleDateString('sv'), slot: row.slot || '' },
    ] : (d.routine_history || []);
    const remaining = d.med_entries.filter(r => r.id !== id);
    const slot = row?.slot;
    if (slot && !remaining.some(r => r.slot === slot)) remaining.push(EMPTY_MED(slot));
    return { ...d, med_entries: remaining, routine_history: history };
  });
  const updateMedRow = (id, val) => setDraft(d => ({
    ...d, med_entries: d.med_entries.map(r => r.id === id ? val : r),
  }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const routinePayload = {
        ...draft,
        feed_schedule_enabled:    feedSchedule,
        meds_schedule_enabled:    medsSchedule,
        turnout_schedule_enabled: turnoutSchedule,
        routine_history:          draft.routine_history || [],
      };
      const res = await api.put(`/farmfolio/horse/${horse.id}/routine`, routinePayload);
      const saved = res.data || {};
      setRoutine({
        ...saved,
        feed_entries: ensureSlots(saved.feed_entries, EMPTY_FEED),
        med_entries:  ensureSlots(saved.med_entries,  EMPTY_MED),
        turnout:  saved.turnout  || { location_id: '', location_name: '', setting_type: '', notes: '' },
        bedding:  saved.bedding  || { type: '' },
        boarding: saved.boarding || { care_catalog_item_id: '', name: '' },
      });
      setEditing(false);
      setDraft(null);

      // Delete ONLY routine items for the categories this routine owns.
      // Other categories (Breeding, Maintenance, etc.) are untouched.
      await Promise.allSettled([
        api.delete(`/farmfolio/scheduled-care/upcoming-routine/${horse.id}?categories=Daily Care`),
        api.delete(`/farmfolio/scheduled-care/upcoming-routine/${horse.id}?categories=Medical`),
        api.delete(`/farmfolio/scheduled-care/upcoming-routine/${horse.id}?categories=Turnout`),
      ]);

      const schedulePromises = [];
      const startDate = new Date();
      startDate.setHours(0, 0, 0, 0);
      const makeDate = (offset) => {
        const d = new Date(startDate.getTime() + offset * 86400000);
        return d.toLocaleDateString('sv');
      };

      if (feedSchedule) {
        draft.feed_entries.filter(r => r.name).forEach(r => {
          const cat = feedCatalog.find(c => c.id === r.care_catalog_item_id);
          for (let d = 0; d < 31; d++) {
            schedulePromises.push(api.post('/farmfolio/scheduled-care', {
              horse_id: horse.id,
              care_catalog_item_id: r.care_catalog_item_id || null,
              category: 'Daily Care', type: r.type || 'Feed', name: r.name,
              dosage:      cat?.dosage      || '',
              dosage_unit: cat?.dosage_unit || '',
              serving_size:       cat?.serving_size ?? null,
              serving_size_units: cat?.serving_size_units || '',
              units: cat?.units != null ? cat.units : null,
              frequency_type: 'once', frequency_config: {},
              start_date: makeDate(d), preferred_time: r.time || '',
              is_routine: true, routine_slot: r.slot,
            }));
          }
        });
      }

      if (medsSchedule) {
        draft.med_entries.filter(r => r.name).forEach(r => {
          const cat = medCatalog.find(c => c.id === r.care_catalog_item_id);
          for (let d = 0; d < 31; d++) {
            schedulePromises.push(api.post('/farmfolio/scheduled-care', {
              horse_id: horse.id,
              care_catalog_item_id: r.care_catalog_item_id || null,
              category: 'Medical', type: r.type || 'Medication', name: r.name,
              dosage:      r.dose || cat?.dosage      || '',
              dosage_unit: cat?.dosage_unit || '',
              units: r.units ? parseFloat(r.units) : (cat?.units != null ? cat.units : null),
              frequency_type: 'once', frequency_config: {},
              start_date: makeDate(d), preferred_time: r.time || '',
              is_routine: true, routine_slot: r.slot,
            }));
          }
        });
      }

      if (turnoutSchedule && draft.turnout?.location_id) {
        for (let d = 0; d < 31; d++) {
          schedulePromises.push(api.post('/farmfolio/scheduled-care', {
            horse_id: horse.id,
            category: 'Daily Care', type: 'Turnout', name: 'Turnout',
            location_id: draft.turnout.location_id,
            setting_type: draft.turnout.setting_type || '',
            notes: draft.turnout.notes || '',
            preferred_time: draft.turnout.start_time || '',
            frequency_type: 'once', frequency_config: {},
            start_date: makeDate(d), is_routine: true,
          }));
        }
      }

      if (schedulePromises.length > 0) {
        await Promise.allSettled(schedulePromises);
      }
      toast.success('Everyday Routine saved');

      if (onSaved) onSaved();
    } catch { toast.error('Failed to save routine'); }
    setSaving(false);
  };

  const src = editing ? draft : routine;

  // ── Slot-grouped table renderer ────────────────────────────────────────────
  const renderFeedRows = (entries, isEditing) => {
    return SLOTS.flatMap(slot => {
      const slotRows = entries.filter(r => r.slot === slot);
      return slotRows.map((row, i) => (
        <FeedRow
          key={row.id || `${slot}-${i}`}
          row={row}
          editing={isEditing}
          feedCatalog={feedCatalog}
          showSlot={i === 0}
          showAdd={isEditing && i === slotRows.length - 1}
          onAdd={() => addFeedRow(slot)}
          showTime={feedSchedule && isEditing}
          onChange={(val) => updateFeedRow(row.id, val)}
          onRemove={() => removeFeedRow(row.id)}
        />
      ));
    });
  };

  const renderMedRows = (entries, isEditing) => {
    return SLOTS.flatMap(slot => {
      const slotRows = entries.filter(r => r.slot === slot);
      return slotRows.map((row, i) => (
        <MedRow
          key={row.id || `${slot}-${i}`}
          row={row}
          editing={isEditing}
          medCatalog={medCatalog}
          showSlot={i === 0}
          showAdd={isEditing && i === slotRows.length - 1}
          onAdd={() => addMedRow(slot)}
          showTime={medsSchedule && isEditing}
          onChange={(val) => updateMedRow(row.id, val)}
          onRemove={() => removeMedRow(row.id)}
        />
      ));
    });
  };

  return (
    <>
      {/* Compact card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between py-3">
          <CardTitle className="text-sm">Everyday Routine</CardTitle>
          <Button variant="ghost" size="icon" className="h-7 w-7"
            onClick={() => setDialogOpen(true)} title="View / edit everyday routine">
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </Button>
        </CardHeader>
      </Card>

      {/* Popup Dialog */}
      <Dialog open={dialogOpen} onOpenChange={v => { if (!v) cancelEdit(); setDialogOpen(v); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Everyday Routine</DialogTitle>
          </DialogHeader>

          {loading ? (
            <p className="text-xs text-center text-muted-foreground py-6">Loading…</p>
          ) : (
            <div className="space-y-4 py-2">

              {/* ── BOARDING ─────────────────────────────────────────── */}
              <div className="space-y-1">
                <p className="text-sm font-semibold">Boarding:</p>
                {editing ? (
                  <div className="space-y-1.5">
                    <Select
                      value={draft.boarding?.care_catalog_item_id || '__none__'}
                      onValueChange={v => {
                        if (v === '__none__') {
                          const old = draft.boarding;
                          const catItem = old?.care_catalog_item_id ? boardingCatalog.find(c => c.id === old.care_catalog_item_id) : null;
                          const history = catItem?.billable && old.care_catalog_item_id ? [
                            ...(draft.routine_history || []),
                            { care_catalog_item_id: old.care_catalog_item_id, category: 'Daily Care', type: 'Boarding', name: catItem.name || old.name || '', cost: catItem.cost || 0, cost_currency: catItem.cost_currency || 'USD', effective_from: old.effective_from || null, effective_to: new Date().toLocaleDateString('sv') },
                          ] : (draft.routine_history || []);
                          setDraft(d => ({ ...d, boarding: { care_catalog_item_id: '', name: '', effective_from: '', is_paused: false }, routine_history: history }));
                        } else {
                          const item = boardingCatalog.find(c => c.id === v);
                          setDraft(d => ({ ...d, boarding: { care_catalog_item_id: v, name: item?.name || '', effective_from: d.boarding?.effective_from || '', is_paused: d.boarding?.is_paused || false } }));
                        }
                      }}
                    >
                      <SelectTrigger className="h-8 text-xs max-w-xs">
                        <SelectValue placeholder="Select boarding option…" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— None —</SelectItem>
                        {boardingCatalog.map(item => {
                          const dosageDetail = item.dosage ? `${item.dosage}${item.dosage_unit ? ' ' + item.dosage_unit : ''}` : '';
                          return <SelectItem key={item.id} value={item.id}>{item.name}{dosageDetail ? ` (${dosageDetail})` : ''}{(item.is_billable ?? item.billable) ? ' (billable)' : ''}</SelectItem>;
                        })}
                      </SelectContent>
                    </Select>
                    {draft.boarding?.care_catalog_item_id && (
                      <div className="flex items-center gap-3 flex-wrap">
                        {boardingCatalog.find(c => c.id === draft.boarding.care_catalog_item_id)?.billable && (
                          <div className="mt-0.5">
                            <div className="text-[8px] text-muted-foreground mb-0.5">Eff. From</div>
                            <input type="date" value={draft.boarding?.effective_from || ''}
                              onChange={e => setDraft(d => ({ ...d, boarding: { ...d.boarding, effective_from: e.target.value } }))}
                              className={`w-28 text-[8px] border rounded px-0.5 h-4 focus:outline-none ${draft.boarding?.effective_from ? 'time-filled' : ''}`} />
                          </div>
                        )}
                        <button type="button"
                          onClick={() => setDraft(d => ({ ...d, boarding: { ...d.boarding, is_paused: !d.boarding.is_paused } }))}
                          className={`flex items-center gap-1 text-xs mt-0.5 ${draft.boarding?.is_paused ? 'text-amber-500' : 'text-muted-foreground hover:text-amber-500'}`}>
                          {draft.boarding?.is_paused ? <PlayCircle className="w-3.5 h-3.5" /> : <PauseCircle className="w-3.5 h-3.5" />}
                          <span>{draft.boarding?.is_paused ? 'Resume' : 'Pause'}</span>
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-xs pl-1">
                    {src?.boarding?.name || <span className="text-muted-foreground italic">None listed</span>}
                  </p>
                )}
              </div>

              {/* ── FEED ─────────────────────────────────────────────── */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-sm font-semibold">Feed:</p>
                  {editing && (
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Checkbox id="feed-schedule" checked={feedSchedule} onCheckedChange={setFeedSchedule} />
                      <label htmlFor="feed-schedule" className="cursor-pointer italic">Add to Schedule</label>
                    </div>
                  )}
                </div>
                <div className="overflow-x-auto border rounded">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-muted/20">
                        <th className="py-1.5 px-2 text-left text-xs font-semibold w-14"></th>
                        <th className="py-1.5 px-3 text-left text-xs font-semibold">Name</th>
                        <th className="py-1.5 px-3 text-left text-xs font-semibold w-28">Quantity</th>
                        {feedSchedule && editing && <th className="py-1.5 px-3 text-left text-xs font-semibold">Time</th>}
                        {editing && <th className="w-7"></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {renderFeedRows(src?.feed_entries || SLOTS.map(EMPTY_FEED), editing)}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* ── MEDS ─────────────────────────────────────────────── */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-sm font-semibold">Meds & Supplements:</p>
                  {editing && (
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Checkbox id="meds-schedule" checked={medsSchedule} onCheckedChange={setMedsSchedule} />
                      <label htmlFor="meds-schedule" className="cursor-pointer italic">Add to Schedule</label>
                    </div>
                  )}
                </div>
                <div className="overflow-x-auto border rounded">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-muted/20">
                        <th className="py-1.5 px-2 text-left text-xs font-semibold w-14"></th>
                        <th className="py-1.5 px-3 text-left text-xs font-semibold">Type</th>
                        <th className="py-1.5 px-3 text-left text-xs font-semibold">Name</th>
                        <th className="py-1.5 px-3 text-left text-xs font-semibold w-24">Quantity</th>
                        {medsSchedule && editing && <th className="py-1.5 px-3 text-left text-xs font-semibold">Time</th>}
                        {editing && <th className="w-7"></th>}
                      </tr>
                    </thead>
                    <tbody>
                      {renderMedRows(src?.med_entries || SLOTS.map(EMPTY_MED), editing)}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* ── TURNOUT ──────────────────────────────────────────── */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold">Turnout:</p>
                  {editing && (
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Checkbox id="turnout-schedule" checked={turnoutSchedule} onCheckedChange={setTurnoutSchedule} />
                      <label htmlFor="turnout-schedule" className="cursor-pointer italic">Add to Schedule</label>
                    </div>
                  )}
                </div>
                {editing ? (
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-0.5">
                        <Label className="text-xs">Location</Label>
                        <Select value={draft.turnout?.location_id || '__none__'}
                          onValueChange={v => setDraft(d => ({ ...d, turnout: { ...d.turnout, location_id: v === '__none__' ? '' : v } }))}>
                          <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Select…" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">None</SelectItem>
                            {(locations || []).map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-0.5">
                        <Label className="text-xs">Setting Type</Label>
                        <Select value={draft.turnout?.setting_type || '__none__'}
                          onValueChange={v => setDraft(d => ({ ...d, turnout: { ...d.turnout, setting_type: v === '__none__' ? '' : v } }))}>
                          <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Select…" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">None</SelectItem>
                            {TURNOUT_SETTINGS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-0.5">
                      <Label className="text-xs">Notes</Label>
                      <Input className="h-7 text-xs" value={draft.turnout?.notes || ''}
                        onChange={e => setDraft(d => ({ ...d, turnout: { ...d.turnout, notes: e.target.value } }))}
                        placeholder="e.g. half day, morning only" />
                    </div>
                    {turnoutSchedule && (
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-0.5">
                          <Label className="text-xs">Start Time</Label>
                          <Input type="time" className={`h-7 text-xs ${draft.turnout?.start_time ? 'time-filled' : ''}`}
                            value={draft.turnout?.start_time || ''}
                            onChange={e => setDraft(d => ({ ...d, turnout: { ...d.turnout, start_time: e.target.value } }))} />
                        </div>
                        <div className="space-y-0.5">
                          <Label className="text-xs">End Time</Label>
                          <Input type="time" className={`h-7 text-xs ${draft.turnout?.end_time ? 'time-filled' : ''}`}
                            value={draft.turnout?.end_time || ''}
                            onChange={e => setDraft(d => ({ ...d, turnout: { ...d.turnout, end_time: e.target.value } }))} />
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-xs space-y-0.5 pl-1">
                    {src?.turnout?.location_name || src?.turnout?.location_id
                      ? <p><span className="font-medium">Location:</span> {src.turnout.location_name || src.turnout.location_id}</p> : null}
                    {src?.turnout?.setting_type
                      ? <p><span className="font-medium">Setting Type:</span> {src.turnout.setting_type}</p> : null}
                    {src?.turnout?.notes
                      ? <p><span className="font-medium">Notes:</span> {src.turnout.notes}</p> : null}
                    {!src?.turnout?.location_id && !src?.turnout?.setting_type && !src?.turnout?.notes && (
                      <p className="text-muted-foreground italic">None listed</p>
                    )}
                  </div>
                )}
              </div>

              {/* ── BEDDING ───────────────────────────────────────────── */}
              <div className="space-y-1">
                <p className="text-sm font-semibold">Bedding:</p>
                {editing ? (
                  <Input className="h-7 text-xs max-w-[200px]" placeholder="e.g. Shavings, Straw"
                    value={draft.bedding?.type || ''}
                    onChange={e => setDraft(d => ({ ...d, bedding: { type: e.target.value } }))} />
                ) : (
                  <p className="text-xs pl-1">
                    {src?.bedding?.type || <span className="text-muted-foreground italic">None listed</span>}
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between pt-4 border-t mt-2">
            {editing ? (
              <>
                <div />
                <div className="flex gap-2">
                  <Button variant="outline" onClick={cancelEdit} disabled={saving}>Cancel</Button>
                  <Button className="bg-primary hover:bg-primary/90 text-white" onClick={handleSave} disabled={saving}>
                    {saving ? 'Saving…' : 'Save'}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={enterEdit} title="Edit routine">
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                <div />
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
